import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { holidayService, leaveService, userService } from '../services/api.service';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectOption } from '../components/ui/select';
import { DatePicker } from '../components/ui/date-picker';
import AppLayout from '../layouts/AppLayout';

const AdminPage = () => {
  // Dashboard stats state
  const [stats, setStats] = useState({
    employeeCount: 0,
    departmentCount: 0,
    pendingLeaveRequestsCount: 0
  });
  const [loadingStats, setLoadingStats] = useState(true);
  const [statsError, setStatsError] = useState('');

  // Holiday management state
  const [holidays, setHolidays] = useState([]);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [newHoliday, setNewHoliday] = useState({
    name: '',
    date: new Date(),
    isRecurring: true
  });
  
  // Leave types management state
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [newLeaveType, setNewLeaveType] = useState({
    name: '',
    description: '',
    defaultAllocation: 0
  });
  
  // UI state
  const [loadingHolidays, setLoadingHolidays] = useState(true);
  const [loadingLeaveTypes, setLoadingLeaveTypes] = useState(true);
  const [holidayError, setHolidayError] = useState('');
  const [leaveTypeError, setLeaveTypeError] = useState('');
  const [holidaySuccess, setHolidaySuccess] = useState('');
  const [leaveTypeSuccess, setLeaveTypeSuccess] = useState('');
  const [addingHoliday, setAddingHoliday] = useState(false);
  const [addingLeaveType, setAddingLeaveType] = useState(false);
  
  // Fetch dashboard stats
  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoadingStats(true);
        
        // Fetch all required data for stats
        const [usersResponse, departmentsResponse, leaveRequestsResponse] = await Promise.all([
          userService.getAllUsers(),
          userService.getDepartments(),
          leaveService.getAllLeaveRequests({ status: 'pending' })
        ]);
        
        // Calculate stats
        const employeeCount = usersResponse.users ? usersResponse.users.length : 0;
        const departmentCount = departmentsResponse.departments ? departmentsResponse.departments.length : 0;
        const pendingLeaveRequestsCount = leaveRequestsResponse.leaveRequests ? leaveRequestsResponse.leaveRequests.length : 0;
        
        setStats({
          employeeCount,
          departmentCount,
          pendingLeaveRequestsCount
        });
        
        setLoadingStats(false);
      } catch (err) {
        console.error('Error fetching dashboard stats:', err);
        setStatsError('Failed to load dashboard statistics');
        setLoadingStats(false);
      }
    };
    
    fetchStats();
  }, []);

  // Fetch holidays
  useEffect(() => {
    const fetchHolidays = async () => {
      try {
        setLoadingHolidays(true);
        const response = await holidayService.getAllHolidays(currentYear);
        setHolidays(response.holidays || []);
        setLoadingHolidays(false);
      } catch (err) {
        console.error('Error fetching holidays:', err);
        setHolidayError('Failed to load holidays');
        setLoadingHolidays(false);
      }
    };
    
    fetchHolidays();
  }, [currentYear]);
  
  // Fetch leave types
  useEffect(() => {
    const fetchLeaveTypes = async () => {
      try {
        setLoadingLeaveTypes(true);
        const response = await leaveService.getLeaveTypes();
        setLeaveTypes(response.leaveTypes || []);
        setLoadingLeaveTypes(false);
      } catch (err) {
        console.error('Error fetching leave types:', err);
        setLeaveTypeError('Failed to load leave types');
        setLoadingLeaveTypes(false);
      }
    };
    
    fetchLeaveTypes();
  }, []);
  
  // Handle year change
  const handleYearChange = (e) => {
    setCurrentYear(parseInt(e.target.value));
  };
  
  // Handle new holiday input change
  const handleHolidayInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewHoliday(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  // Handle date change for new holiday
  const handleHolidayDateChange = (date) => {
    setNewHoliday(prev => ({
      ...prev,
      date
    }));
  };
  
  // Handle new leave type input change
  const handleLeaveTypeInputChange = (e) => {
    const { name, value } = e.target;
    setNewLeaveType(prev => ({
      ...prev,
      [name]: name === 'defaultAllocation' ? parseInt(value) : value
    }));
  };
  
  // Add new holiday
  const handleAddHoliday = async (e) => {
    e.preventDefault();
    setHolidayError('');
    setHolidaySuccess('');
    
    if (!newHoliday.name.trim()) {
      setHolidayError('Holiday name is required');
      return;
    }
    
    // Format date using local timezone - defined outside try block so it's available everywhere
    const formatDateToLocalString = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    try {
      setAddingHoliday(true);
      
      const holidayData = {
        name: newHoliday.name,
        date: formatDateToLocalString(newHoliday.date),
        isRecurring: newHoliday.isRecurring
      };
      
      const response = await holidayService.createHoliday(holidayData);
      
      // Convert the response to match UI expectations
      const formattedHoliday = {
        id: response.holiday.id,
        name: response.holiday.name,
        date: response.holiday.date,
        is_recurring: response.holiday.isRecurring
      };
      
      // Update holidays list with the new holiday
      setHolidays(prev => [...prev, formattedHoliday]);
      
      // Reset form
      setNewHoliday({
        name: '',
        date: new Date(),
        isRecurring: true
      });
      
      setHolidaySuccess('Holiday added successfully');
      setAddingHoliday(false);
    } catch (err) {
      console.error('Error adding holiday:', err);
      
      // Better error message for date conflicts
      if (err.response?.data?.message && err.response.data.message.includes('already exists on this date')) {
        const holidayName = err.response?.data?.message.match(/\((.*?)\)/)?.[1] || "Another holiday";
        const yourDate = formatDateToLocalString(newHoliday.date);
        
        setHolidayError(`${holidayName} is already scheduled for this date (${yourDate}). Please choose a different date.`);
      } else {
        setHolidayError(err.response?.data?.message || 'Failed to add holiday');
      }
      
      setAddingHoliday(false);
    }
  };
  
  // Delete holiday
  const handleDeleteHoliday = async (id) => {
    if (!window.confirm('Are you sure you want to delete this holiday?')) {
      return;
    }
    
    try {
      await holidayService.deleteHoliday(id);
      
      // Remove deleted holiday from the list
      setHolidays(prev => prev.filter(h => h.id !== id));
      
      setHolidaySuccess('Holiday deleted successfully');
    } catch (err) {
      console.error('Error deleting holiday:', err);
      setHolidayError('Failed to delete holiday');
    }
  };
  
  // Add new leave type
  const handleAddLeaveType = async (e) => {
    e.preventDefault();
    setLeaveTypeError('');
    setLeaveTypeSuccess('');
    
    if (!newLeaveType.name.trim()) {
      setLeaveTypeError('Leave type name is required');
      return;
    }
    
    try {
      setAddingLeaveType(true);
      
      // Create leave type data object
      const leaveTypeData = {
        name: newLeaveType.name,
        description: newLeaveType.description,
        defaultAllocation: newLeaveType.defaultAllocation || 0
      };
      
      // Make API call to create leave type
      const response = await leaveService.createLeaveType(leaveTypeData);
      
      console.log('Leave type created:', response);
      
      // Format the response for UI
      const newType = {
        id: response.leaveType.id,
        name: response.leaveType.name,
        description: response.leaveType.description,
        default_allocation: response.leaveType.default_allocation
      };
      
      // Add to state
      setLeaveTypes(prev => [...prev, newType]);
      
      // Reset form
      setNewLeaveType({
        name: '',
        description: '',
        defaultAllocation: 0
      });
      
      setLeaveTypeSuccess('Leave type added successfully');
      setAddingLeaveType(false);
    } catch (err) {
      console.error('Error adding leave type:', err);
      
      // Set appropriate error message
      if (err.response?.data?.message) {
        setLeaveTypeError(err.response.data.message);
      } else {
        setLeaveTypeError('Failed to add leave type');
      }
      
      setAddingLeaveType(false);
    }
  };
  
  // Format date
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  return (
    <AppLayout>
      <div className="space-y-8">
        <h1 className="text-2xl font-bold">System Administration</h1>
        
        {statsError && (
          <div className="bg-red-50 text-red-500 p-3 rounded-md">
            {statsError}
          </div>
        )}
        
        {/* Admin Dashboard Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-6">
            <h2 className="text-xl font-bold mb-2">Employees</h2>
            {loadingStats ? (
              <p className="text-3xl font-bold mb-4">Loading...</p>
            ) : (
              <p className="text-3xl font-bold mb-4">{stats.employeeCount}</p>
            )}
            <Link to="/staff">
              <Button variant="outline">Manage Staff</Button>
            </Link>
          </Card>
          
          <Card className="p-6">
            <h2 className="text-xl font-bold mb-2">Departments</h2>
            {loadingStats ? (
              <p className="text-3xl font-bold mb-4">Loading...</p>
            ) : (
              <p className="text-3xl font-bold mb-4">{stats.departmentCount}</p>
            )}
            <Link to="/admin/departments">
              <Button variant="outline">Manage Departments</Button>
            </Link>
          </Card>
          
          <Card className="p-6">
            <h2 className="text-xl font-bold mb-2">Pending Requests</h2>
            {loadingStats ? (
              <p className="text-3xl font-bold mb-4">Loading...</p>
            ) : (
              <p className="text-3xl font-bold mb-4">{stats.pendingLeaveRequestsCount}</p>
            )}
            <Link to="/leave-requests">
              <Button variant="outline">View Requests</Button>
            </Link>
          </Card>
        </div>
        
        {/* Holiday Management */}
        <div className="space-y-4">
          <h2 className="text-xl font-bold">Holiday Management</h2>
          
          {holidayError && (
            <div className="bg-red-50 text-red-500 p-3 rounded-md">
              {holidayError}
            </div>
          )}
          
          {holidaySuccess && (
            <div className="bg-green-50 text-green-700 p-3 rounded-md">
              {holidaySuccess}
            </div>
          )}
          
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Holidays</CardTitle>
                <Select 
                  value={currentYear} 
                  onChange={handleYearChange}
                  className="w-32"
                >
                  {Array.from({ length: 5 }, (_, i) => currentYear - 2 + i).map(year => (
                    <SelectOption key={year} value={year}>{year}</SelectOption>
                  ))}
                </Select>
              </div>
            </CardHeader>
            
            <CardContent>
              {loadingHolidays ? (
                <div className="text-center py-4">Loading holidays...</div>
              ) : (
                <div className="space-y-4">
                  {holidays.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {holidays.map(holiday => (
                        <div key={holiday.id} className="p-4 border rounded-lg">
                          <div className="flex justify-between items-start">
                            <div>
                              <h3 className="font-medium">{holiday.name}</h3>
                              <p className="text-sm text-gray-500">{formatDate(holiday.date)}</p>
                              {holiday.is_recurring && (
                                <span className="mt-1 inline-block px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                                  Annual
                                </span>
                              )}
                            </div>
                            <Button 
                              variant="destructive" 
                              size="sm"
                              onClick={() => handleDeleteHoliday(holiday.id)}
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-gray-500">
                      No holidays found for {currentYear}
                    </div>
                  )}
                  
                  {/* Add New Holiday Form */}
                  <div className="mt-6 p-4 border rounded-lg">
                    <h3 className="font-medium mb-4">Add New Holiday</h3>
                    <form onSubmit={handleAddHoliday} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="text-sm font-medium" htmlFor="holidayName">
                            Holiday Name
                          </label>
                          <Input
                            id="holidayName"
                            name="name"
                            placeholder="e.g., Christmas Day"
                            value={newHoliday.name}
                            onChange={handleHolidayInputChange}
                            disabled={addingHoliday}
                            required
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <label className="text-sm font-medium">
                            Date
                          </label>
                          <DatePicker
                            date={newHoliday.date}
                            setDate={handleHolidayDateChange}
                            disabled={addingHoliday}
                          />
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="isRecurring"
                          name="isRecurring"
                          checked={newHoliday.isRecurring}
                          onChange={handleHolidayInputChange}
                          className="rounded border-gray-300"
                          disabled={addingHoliday}
                        />
                        <label htmlFor="isRecurring" className="text-sm font-medium">
                          Annual recurring holiday
                        </label>
                      </div>
                      
                      <Button 
                        type="submit" 
                        disabled={addingHoliday}
                      >
                        {addingHoliday ? 'Adding...' : 'Add Holiday'}
                      </Button>
                    </form>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        {/* Leave Types Management */}
        <div className="space-y-4">
          <h2 className="text-xl font-bold">Leave Types Management</h2>
          
          {leaveTypeError && (
            <div className="bg-red-50 text-red-500 p-3 rounded-md">
              {leaveTypeError}
            </div>
          )}
          
          {leaveTypeSuccess && (
            <div className="bg-green-50 text-green-700 p-3 rounded-md">
              {leaveTypeSuccess}
            </div>
          )}
          
          <Card>
            <CardHeader>
              <CardTitle>Leave Types</CardTitle>
            </CardHeader>
            
            <CardContent>
              {loadingLeaveTypes ? (
                <div className="text-center py-4">Loading leave types...</div>
              ) : (
                <div className="space-y-4">
                  {leaveTypes.length > 0 ? (
                    <div className="grid grid-cols-1 gap-4">
                      {leaveTypes.map(leaveType => (
                        <div key={leaveType.id} className="p-4 border rounded-lg">
                          <div className="flex justify-between items-start">
                            <div>
                              <h3 className="font-medium">{leaveType.name}</h3>
                              <p className="text-sm text-gray-500">
                                {leaveType.description || 'No description provided'}
                              </p>
                              <p className="text-sm mt-2">
                                Default Allocation: <span className="font-medium">{leaveType.default_allocation || 0} days</span>
                              </p>
                            </div>
                            <Button 
                              variant="outline" 
                              size="sm"
                            >
                              Edit
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-gray-500">
                      No leave types found
                    </div>
                  )}
                  
                  {/* Add New Leave Type Form */}
                  <div className="mt-6 p-4 border rounded-lg">
                    <h3 className="font-medium mb-4">Add New Leave Type</h3>
                    <form onSubmit={handleAddLeaveType} className="space-y-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium" htmlFor="leaveTypeName">
                          Leave Type Name
                        </label>
                        <Input
                          id="leaveTypeName"
                          name="name"
                          placeholder="e.g., Parental Leave"
                          value={newLeaveType.name}
                          onChange={handleLeaveTypeInputChange}
                          disabled={addingLeaveType}
                          required
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <label className="text-sm font-medium" htmlFor="leaveTypeDescription">
                          Description (Optional)
                        </label>
                        <Input
                          id="leaveTypeDescription"
                          name="description"
                          placeholder="Describe this leave type"
                          value={newLeaveType.description}
                          onChange={handleLeaveTypeInputChange}
                          disabled={addingLeaveType}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <label className="text-sm font-medium" htmlFor="defaultAllocation">
                          Default Allocation (Days)
                        </label>
                        <Input
                          id="defaultAllocation"
                          name="defaultAllocation"
                          type="number"
                          min="0"
                          placeholder="0"
                          value={newLeaveType.defaultAllocation}
                          onChange={handleLeaveTypeInputChange}
                          disabled={addingLeaveType}
                        />
                      </div>
                      
                      <Button 
                        type="submit" 
                        disabled={addingLeaveType}
                      >
                        {addingLeaveType ? 'Adding...' : 'Add Leave Type'}
                      </Button>
                    </form>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        {/* System Settings (Placeholder) */}
        <div className="space-y-4">
          <h2 className="text-xl font-bold">System Settings</h2>
          
          <Card className="p-6">
            <p className="text-gray-500">
              Additional system settings will be implemented in future updates.
            </p>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
};

export default AdminPage;